import React, {FC} from "react"
import Flex from "@irati/ui/src/Flex"
import {Body, Label, H4, H1} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import authentication from "@irati/auth"
import mySavingsTracking from "@irati-tracking/my-savings/mySavings"
import PropTypes from "prop-types"
import Loading from "@irati/ui/src/Loading"
import Button, {IconTypes} from "@irati/ui/src/Button"
import History from "@irati/utils/history"
import Icon from "@irati/ui/src/Icon"
import {useUserSavings} from "@irati/adminReporting/hooks"
import {RedemptionReportDuration} from "@irati/types/generated/ops"
import BreakdownDetails from "../../BreakdownDetails"
import {StyledCard, TotalSavings} from "./styles"

interface Props {
  showInHeader?: boolean
}

const MySavingsCard: FC<Props> = ({showInHeader}, {apps, app}) => {
  const {data, load} = useUserSavings({month: undefined, duration: RedemptionReportDuration.AllTime})
  if (!data) return null

  const createdAt = authentication.user()?.metadata?.createdAt!
  const createdAtDate = new Date(createdAt)
  const comparisonDate = new Date(data?.comparisonDate!)

  const handleClick = (type: string) => {
    mySavingsTracking.clickSavingsTrackerBtn("start_saving_cta", app, type)
    History.push({pathname: `/categories?sort=popularity`}, apps.PERKS)
  }

  return (
    <Loading load={load} initLoad>
      <StyledCard showInHeader={showInHeader}>
        <Flex direction="column" gap={1.5}>
          <Flex justifyContent="space-between">
            <H4>
              <FormattedMessage id="platform.mySavings.header.copy" />
            </H4>
            {!showInHeader && <Icon icon={IconTypes.savingsAnimated} size={28} />}
          </Flex>
          <div>
            {data?.savings?.fixed?.value ? (
              <TotalSavings>
                <FormattedCurrency
                  value={data?.savings?.fixed?.value}
                  currency={data?.savings?.fixed?.currency}
                  maximumFractionDigits={data?.savings?.fixed?.value ? 2 : 0}
                />{" "}
                *
              </TotalSavings>
            ) : (
              <H1>
                <FormattedMessage id="platform.mySavings.zeroState.title" />
                <span>*</span>
              </H1>
            )}

            {/* The code below may change in the future if the user activation date will be available */}
            <Body small>
              {createdAtDate < comparisonDate ? (
                <FormattedMessage id="platform.mySavings.userActivationDate.before" />
              ) : (
                <FormattedMessage id="platform.mySavings.userActivationDate.after" />
              )}
            </Body>
          </div>
          <BreakdownDetails breakdown={data?.savings?.fixed?.breakdown} showV2Details breakdownTotalValue={data?.savings?.fixed?.value} />
          {data?.savings?.fixed?.value === 0 ? (
            <Button primary onClick={() => handleClick("zero_savings")}>
              <FormattedMessage id="platform.mySavings.zeroState.breakdown.startSaving" />
            </Button>
          ) : (
            <Button primary onClick={() => handleClick("increase_savings")}>
              <FormattedMessage id="platform.mySavings.cta.goToPerks.copy" />
            </Button>
          )}
          <Label>
            {authentication.apps("wallet.treatTokens") ? (
              <FormattedMessage id="platform.mySavings.info.message" />
            ) : (
              <FormattedMessage id="platform.mySavings.info.messageNoFp" />
            )}
          </Label>
        </Flex>
      </StyledCard>
    </Loading>
  )
}

MySavingsCard.contextTypes = {
  apps: PropTypes.object.isRequired,
  app: PropTypes.string.isRequired
}

export default MySavingsCard
